import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import type { Profile } from './types'

/**
 * What the caller may do INSIDE a bid — the per-bid permission axis, read from the memberships
 * `GET /v1/me` now returns.
 *
 * This is an AFFORDANCE, never a gate. The server already refuses: the REST write surface, the
 * revision lock and the CRDT handshake all check the same profile server-side. What this exists for
 * is not showing people buttons that will 403 at them.
 *
 * Not to be confused with `Me.roles`, which is TENANT-level and drives corporate-KB ACL trimming.
 * A caller holding the tenant `admin` role bypasses per-bid checks server-side, so it does here too
 * — otherwise the recovery path for a bid whose last admin left would be invisible in the UI.
 */
export function useBidProfile(bid: string | null): {
  profile: Profile | null
  isAdmin: boolean
  /**
   * The TENANT-level `admin` role alone — unlike `isAdmin`, a bid's own `admin` profile does NOT
   * qualify here. A few capabilities are deliberately gated stricter than "admin of this bid" (e.g.
   * raising a bid's click-to-index chunk ceiling, plans/BID_SITE_INDEXING.md): they are cost/ops
   * decisions for the whole tenant, not editing rights inside one bid.
   */
  isTenantAdmin: boolean
  canEdit: boolean
  isViewer: boolean
  /** False until `/v1/me` has answered — treat the UI as unknown, not as "viewer", until then. */
  isResolved: boolean
} {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 })

  // Optional-chain BOTH: this hook is mounted by App itself, so a /v1/me payload missing either
  // field would throw during render and take the entire app down rather than degrade.
  const isTenantAdmin = me.data?.roles?.includes('admin') ?? false
  const membership = bid ? me.data?.memberships?.find((m) => m.bidId === bid) : undefined
  const profile: Profile | null = isTenantAdmin ? 'admin' : (membership?.profile ?? null)

  return {
    profile,
    isAdmin: profile === 'admin',
    isTenantAdmin,
    canEdit: profile === 'admin' || profile === 'editor',
    isViewer: profile === 'viewer',
    isResolved: Boolean(me.data) && Boolean(bid),
  }
}
